/**
 * Toast notification store — ephemeral messages that auto-dismiss.
 * Includes game sound effects with streak escalation.
 *
 * Variants match the PHP app's toast modifiers:
 *   submit (pink), join (cyan), follow (green), points (purple), error (pink)
 */

export type ToastVariant = "submit" | "join" | "follow" | "points" | "error";

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  dismissing: boolean;
}

export interface ToastOptions {
  points?: number;
}

let _toasts = $state<Toast[]>([]);
let _nextId = 0;

export function getToasts(): Toast[] {
  return _toasts;
}

export function addToast(
  message: string,
  variant: ToastVariant = "join",
  durationMs = 4500,
  options?: ToastOptions,
): void {
  const id = ++_nextId;
  _toasts = [..._toasts, { id, message, variant, dismissing: false }];
  setTimeout(() => dismissToast(id), durationMs);

  // Play sound
  playToastSound(variant, options?.points);
}

export function dismissToast(id: number): void {
  _toasts = _toasts.map((t) => (t.id === id ? { ...t, dismissing: true } : t));
  setTimeout(() => {
    _toasts = _toasts.filter((t) => t.id !== id);
  }, 300);
}

// ── Sound System ─────────────────────────────────────────────────────────

const SOUND_URLS: Record<string, string> = {
  blip: "/sounds/blip.mp3",
  coin: "/sounds/coin.mp3",
  "coin-high": "/sounds/coin-high.mp3",
  fanfare: "/sounds/fanfare.mp3",
};

const SOUND_TIERS = ["coin", "coin-high", "fanfare"] as const;

let audioCtx: AudioContext | null = null;
const audioCache: Record<string, AudioBuffer> = {};
let lastPointsTime = 0;
let streakLevel = 0;

function initAudio(): void {
  if (audioCtx) return;
  try {
    audioCtx = new AudioContext();
    for (const [key, url] of Object.entries(SOUND_URLS)) {
      fetch(url)
        .then((r) => r.arrayBuffer())
        .then((b) => audioCtx!.decodeAudioData(b))
        .then((buf) => {
          audioCache[key] = buf;
        })
        .catch(() => {}); // Non-fatal — sounds are optional
    }
  } catch {
    // AudioContext not supported — sounds disabled silently
  }
}

function playSound(key: string): void {
  if (!audioCtx || !audioCache[key]) return;
  if (
    typeof localStorage !== "undefined" &&
    localStorage.getItem("beatduel_sounds") === "off"
  )
    return;
  try {
    if (audioCtx.state === "suspended") audioCtx.resume();
    const source = audioCtx.createBufferSource();
    source.buffer = audioCache[key];
    source.connect(audioCtx.destination);
    source.start();
  } catch {
    // Non-fatal
  }
}

function getPointsSoundKey(points: number): string {
  if (points >= 20) return "fanfare";
  if (points >= 6) return "coin-high";
  return "coin";
}

function playToastSound(variant: ToastVariant, points?: number): void {
  // Init audio on first toast (user has interacted by now)
  initAudio();

  if (variant === "error") return; // Errors are silent

  if (variant === "points") {
    const now = Date.now();
    // Streak: consecutive points toasts within 10 seconds bump the tier
    if (now - lastPointsTime < 10000) {
      streakLevel = Math.min(streakLevel + 1, 2);
    } else {
      streakLevel = 0;
    }
    lastPointsTime = now;

    // Base tier from points amount
    const baseKey = getPointsSoundKey(points ?? 5);
    const baseIdx = SOUND_TIERS.indexOf(
      baseKey as (typeof SOUND_TIERS)[number],
    );

    // Bump by streak level, cap at fanfare
    const finalIdx = Math.min(baseIdx + streakLevel, SOUND_TIERS.length - 1);
    playSound(SOUND_TIERS[finalIdx]);
  } else {
    // Non-points: subtle blip
    playSound("blip");
  }
}

// Export for settings toggle
export function isSoundEnabled(): boolean {
  if (typeof localStorage === "undefined") return true;
  return localStorage.getItem("beatduel_sounds") !== "off";
}

export function setSoundEnabled(enabled: boolean): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem("beatduel_sounds", enabled ? "on" : "off");
}
